import { FC } from 'react';

import { usePGUnitsQuery } from 'Dictionary/query/dictionaryQuery';
import { mapToAutocompleteFieldItems } from 'Dictionary/model/PGUnit';
import {
  FormikAutocompleteField,
  FormikAutocompleteFieldProps,
} from 'components/FormikField/FormikAutocompleteField/FormikAutocompleteField';
import { useMyProfileQuery } from 'MyProfile/query/myProfile.query';

export interface PGUnitSelectFieldProps
  extends Omit<FormikAutocompleteFieldProps, 'options'> {
  name: string;
  businessUnitID: number;
  disabled?: boolean;
  addAllOption?: boolean;
}

export const PGUnitSelectField: FC<PGUnitSelectFieldProps> = ({
  name,
  businessUnitID,
  disabled = false,
  multiple = false,
  addAllOption = false,
  ...otherProps
}) => {
  const { data = [], isLoading, refetch } = usePGUnitsQuery(businessUnitID);
  const { data: userData } = useMyProfileQuery();
  const isAdmin = userData?.isAdmin;
  const localAdminRole = userData?.roles?.find((r) => r.type === 'LocalAdministrator');
  const isLocalAdmin = !!localAdminRole;
  const allowedProductGroups = userData?.roles?.flatMap(role =>
    role.details?.map(detail => detail?.description2?.split('-')?.[1]?.trim())
  ).filter(Boolean) ?? [];
  const normalizedAllowedProductGroups = allowedProductGroups.map(x => x?.trim().toLowerCase());
  const getProductGroupName = (value?: string) => {
    if (!value) {
      return '';
    }
    const parts = value.trim().split(/\s+/);
    return (parts.length > 1 ? parts.slice(1).join(' ') : parts[0]).trim().toLowerCase();
  };
  const hasAllProductGroupsAccess = normalizedAllowedProductGroups.includes('all product groups');

  const mappedItems = mapToAutocompleteFieldItems(data);
  const shouldFilter = !isAdmin && isLocalAdmin && !hasAllProductGroupsAccess;
  const filteredItems = shouldFilter
    ? mappedItems.filter(item => normalizedAllowedProductGroups.includes(getProductGroupName(item.name)))
    : mappedItems;
  const shouldAddAll = addAllOption && (isAdmin || hasAllProductGroupsAccess);
  const allOption = { id: 0, name: 'All Product Groups' };
  const options = shouldAddAll ? [allOption, ...filteredItems] : filteredItems;

  return (
    <FormikAutocompleteField
      name={name}
      disabled={disabled}
      options={options}
      loading={isLoading}
      onOpen={() => refetch()}
      multiple={multiple}
      {...otherProps}
    />
  );
};
